import React from 'react';
import type { Worker } from '../../api/workersApi';
import type { Handler } from '../../api/handlersApi';
import { buildHandlerOptionsForWorker, latestRefFor, resolveHandlerRef } from '../../utils/handlerRefs';
import type { StepNode, StepType } from './types';
import type { Edge } from './types';
import { formatJson } from './layout';
import { LabeledInput, LabeledSelect, LabeledTextarea } from './Fields';

const VERSIONED_HANDLER_KEY_RE = /(?:\.v\d+|:v?\d+)$/i;

function preferLatestHandlerRef(handlerKeyOrRef: string): string {
  const ref = String(handlerKeyOrRef ?? '').trim();
  if (!ref) return '';
  if (/:latest$/i.test(ref)) return ref;
  // Only coerce versioned keys to :latest to avoid breaking non-versioned system handlers.
  if (!VERSIONED_HANDLER_KEY_RE.test(ref)) return ref;
  return latestRefFor(ref) || ref;
}

interface Props {
  selectedNode?: StepNode;
  nodes: StepNode[];
  edges: Edge[];
  workers: Worker[];
  workersLoading: boolean;
  workersError: Error | null;
  handlers: Handler[];
  handlersLoading: boolean;
  handlersError: Error | null;
  deviceConfigKeys: string[];
  deviceConfigKeysLoading: boolean;
  deviceConfigKeysError: Error | null;
  inputsSchemaJson: string;
  onUpdate: (patch: {
    type?: StepType;
    service?: string;
    device_capability?: string;
    when?: {
      has?: string[];
      has_any?: string[];
    };
    retries?: { count: number; backoff: string };
    paramsDefaultsJson?: string;
    paramsBindingsJson?: string;
    overrideAllowlistJson?: string;
  }) => void;
  onEnsureEdge?: (fromStepId: string, toStepId: string) => void;
  onRenameStep: (oldId: string, nextId: string) => void;
  stepKeyDraft: string;
  onChangeStepKeyDraft: (next: string) => void;
}

type ParamSource = 'default' | 'runtime_input' | 'preset' | 'device_config' | 'upstream_output';

function unwrapTemplate(value: string): string | null {
  const v = value.trim();
  const m = v.match(/^\{\{\s*([\s\S]+?)\s*\}\}$/);
  return m?.[1]?.trim() ?? null;
}

function getRuntimeInputTransforms(binding: unknown): { sanitize: boolean; datePrefix: boolean } {
  if (typeof binding !== 'string') return { sanitize: false, datePrefix: false };
  const v = binding;
  return {
    sanitize: /sanitize_filename\s*\(/.test(v),
    datePrefix: /(date_prefix_yymmdd|date_prefix)\s*\(/.test(v),
  };
}

function buildRuntimeBinding(key: string, opts?: { sanitize?: boolean; datePrefix?: boolean }): string {
  const safeKey = key.trim();
  let inner = `inputs.${safeKey}`;
  if (opts?.sanitize) {
    inner = `sanitize_filename(${inner})`;
  }
  if (opts?.datePrefix) {
    inner = `date_prefix_yymmdd(${inner})`;
  }
  return `{{ ${inner} }}`;
}

function buildPresetBinding(key: string, opts?: { sanitize?: boolean; datePrefix?: boolean }): string {
  const safeKey = key.trim();
  let inner = `preset.${safeKey}`;
  if (opts?.sanitize) {
    inner = `sanitize_filename(${inner})`;
  }
  if (opts?.datePrefix) {
    inner = `date_prefix_yymmdd(${inner})`;
  }
  return `{{ ${inner} }}`;
}

function buildDeviceBinding(path: string): string {
  const safePath = path.trim();
  return `{{ device.${safePath} }}`;
}

function normalizeHandlerConfig(config: unknown): unknown {
  if (config === undefined || config === null) return {};
  if (typeof config === 'string') {
    const trimmed = config.trim();
    if (!trimmed) return {};
    try {
      return JSON.parse(trimmed);
    } catch {
      return {};
    }
  }

  // Some backends store the handler config wrapped, e.g. { config: { params_schema: ... } }
  // or { handler_config: { params_schema: ... } }. Unwrap these so the UI can reliably
  // read params_schema/outputs_schema.
  if (config && typeof config === 'object' && !Array.isArray(config)) {
    const obj = config as Record<string, any>;
    const looksLikeHandlerConfig = (v: unknown): boolean => {
      if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
      const vv = v as Record<string, any>;
      return typeof vv.params_schema === 'object' || typeof vv.outputs_schema === 'object' || Array.isArray(vv.outputs);
    };

    if (looksLikeHandlerConfig(obj)) return obj;

    const innerConfig = obj.config;
    if (looksLikeHandlerConfig(innerConfig)) return innerConfig;

    const innerHandlerConfig = obj.handler_config;
    if (looksLikeHandlerConfig(innerHandlerConfig)) return innerHandlerConfig;

    // Sometimes config-only producers embed handler_config under config.handler_config.
    if (innerConfig && typeof innerConfig === 'object' && !Array.isArray(innerConfig)) {
      const nested = (innerConfig as Record<string, any>).handler_config;
      if (looksLikeHandlerConfig(nested)) return nested;
    }
  }

  return config;
}

function coerceObject(value: unknown): Record<string, any> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : {};
}

function tryParseJsonObject(text: string): Record<string, any> {
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function tryParseJsonArray(text: string): string[] {
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function listSchemaProperties(schema: unknown): string[] {
  const obj = coerceObject(schema);
  const props = coerceObject(obj.properties);
  return Object.keys(props);
}

function listSchemaRequired(schema: unknown): Set<string> {
  const obj = coerceObject(schema);
  const required = Array.isArray(obj.required) ? obj.required : [];
  return new Set(required.filter((x) => typeof x === 'string'));
}

function schemaPropertyIsString(schema: unknown, key: string): boolean {
  const obj = coerceObject(schema);
  const props = coerceObject(obj.properties);
  const prop = coerceObject(props[key]);
  const t = prop.type;
  if (typeof t === 'string') return t === 'string';
  if (Array.isArray(t)) return t.some((x) => x === 'string');
  return false;
}

function listParamKeysFromParamsSchema(paramsSchema: unknown): string[] {
  const obj = coerceObject(paramsSchema);
  const mandatory = coerceObject(obj.mandatory);
  const optional = coerceObject(obj.optional);
  const keys = new Set<string>([...Object.keys(mandatory), ...Object.keys(optional)]);
  return Array.from(keys).sort();
}

function listRequiredParamsFromParamsSchema(paramsSchema: unknown): Set<string> {
  const obj = coerceObject(paramsSchema);
  const mandatory = coerceObject(obj.mandatory);
  const keys = new Set<string>(Object.keys(mandatory));

  // Support an alternative shape where optional/mandatory entries include { required: true }.
  Object.entries(mandatory).forEach(([k, v]) => {
    const def = coerceObject(v);
    if (def.required === false) keys.delete(k);
  });
  return keys;
}

function getParamDefinition(paramsSchema: unknown, param: string): { type?: string; description?: string; default?: unknown } {
  const obj = coerceObject(paramsSchema);
  const mandatory = coerceObject(obj.mandatory);
  const optional = coerceObject(obj.optional);
  const def = (mandatory[param] ?? optional[param]);
  const d = coerceObject(def);
  return {
    type: typeof d.type === 'string' ? d.type : undefined,
    description: typeof d.description === 'string' ? d.description : undefined,
    default: Object.prototype.hasOwnProperty.call(d, 'default') ? d.default : undefined,
  };
}

function listHandlerOutputs(config: unknown): string[] {
  const cfg = coerceObject(normalizeHandlerConfig(config));
  const outputsList = Array.isArray(cfg.outputs) ? cfg.outputs.filter((x) => typeof x === 'string') : [];
  const outputsSchema = coerceObject(cfg.outputs_schema);
  const props = coerceObject(outputsSchema.properties);
  const keys = Object.keys(props);
  return keys.length > 0 ? keys : outputsList;
}

function bindingKind(value: unknown): ParamSource {
  if (typeof value !== 'string') return 'default';
  const inner = unwrapTemplate(value);
  if (!inner) return 'default';
  if (inner.startsWith('steps.')) return 'upstream_output';
  if (inner.includes('device.')) return 'device_config';
  if (inner.includes('preset.')) return 'preset';
  if (inner.includes('inputs.')) return 'runtime_input';
  return 'default';
}

function getRuntimeInputKey(binding: string): string {
  const inner = unwrapTemplate(binding);
  if (!inner) return '';
  const m = inner.match(/\binputs\.([a-zA-Z0-9_-]+)\b/);
  return m?.[1] ?? '';
}

function getPresetKey(binding: string): string {
  const inner = unwrapTemplate(binding);
  if (!inner) return '';
  const m = inner.match(/\bpreset\.([a-zA-Z0-9_-]+)\b/);
  return m?.[1] ?? '';
}

function getDevicePath(binding: string): string {
  const inner = unwrapTemplate(binding);
  if (!inner) return '';
  const m = inner.match(/\bdevice\.([a-zA-Z0-9_.+-]+)\b/);
  return m?.[1] ?? '';
}

function getUpstreamRef(binding: string): { stepId: string; outputKey: string } {
  const m = binding.match(/^\{\{\s*steps\.([a-zA-Z0-9_-]+)\.outputs\.([a-zA-Z0-9_-]+)\s*\}\}$/);
  return { stepId: m?.[1] ?? '', outputKey: m?.[2] ?? '' };
}

export default function StepEditorCard({ selectedNode, nodes, edges, workers, workersLoading, workersError, handlers, handlersLoading, handlersError, deviceConfigKeys, deviceConfigKeysLoading, deviceConfigKeysError, inputsSchemaJson, onUpdate, onEnsureEdge, onRenameStep, stepKeyDraft, onChangeStepKeyDraft }: Props) {
  const [workerId, setWorkerId] = React.useState<string>('');
  const [defaultDraftByParam, setDefaultDraftByParam] = React.useState<Record<string, string>>({});
  const [sourceDraftByParam, setSourceDraftByParam] = React.useState<Record<string, ParamSource>>({});
  const [runtimeKeyDraftByParam, setRuntimeKeyDraftByParam] = React.useState<Record<string, string>>({});
  const [presetKeyDraftByParam, setPresetKeyDraftByParam] = React.useState<Record<string, string>>({});
  const [devicePathDraftByParam, setDevicePathDraftByParam] = React.useState<Record<string, string>>({});
  const [upstreamDraftByParam, setUpstreamDraftByParam] = React.useState<Record<string, { stepId: string; outputKey: string }>>({});

  const inputsSchema = React.useMemo(() => {
    try {
      const parsed = JSON.parse(inputsSchemaJson || '{}');
      return parsed;
    } catch {
      return {};
    }
  }, [inputsSchemaJson]);

  const workflowInputKeys = React.useMemo(() => {
    const keys = listSchemaProperties(inputsSchema);
    keys.sort((a, b) => a.localeCompare(b));
    return keys;
  }, [inputsSchema]);

  const selectedWhenHas = React.useMemo(() => {
    return (selectedNode?.def?.when?.has ?? []).map((s) => String(s ?? '').trim()).filter(Boolean);
  }, [selectedNode?.id, selectedNode?.def?.when]);

  const toggleWhenKey = React.useCallback((key: string, nextChecked: boolean) => {
    if (!selectedNode) return;
    const normalizedKey = String(key ?? '').trim();
    if (!normalizedKey) return;

    const prevWhen = selectedNode.def.when ?? {};
    const prevHas = (prevWhen.has ?? []).map((s) => String(s ?? '').trim()).filter(Boolean);
    const set = new Set(prevHas);
    if (nextChecked) set.add(normalizedKey);
    else set.delete(normalizedKey);
    const nextHas = Array.from(set).sort((a, b) => a.localeCompare(b));

    const hasAny = (prevWhen.has_any ?? []).map((s) => String(s ?? '').trim()).filter(Boolean);
    if (nextHas.length === 0 && hasAny.length === 0) {
      onUpdate({ when: undefined });
      return;
    }

    onUpdate({
      when: {
        ...prevWhen,
        has: nextHas.length ? nextHas : undefined,
        has_any: hasAny.length ? hasAny : undefined,
      },
    });
  }, [onUpdate, selectedNode]);

  // Keep worker selection in sync with current handler selection (when possible)
  React.useEffect(() => {
    if (!selectedNode) return;
    const currentHandlerKey = selectedNode.def.service?.trim();
    if (!currentHandlerKey) return;
    const match = resolveHandlerRef(handlers, currentHandlerKey);
    if (match && String(match.worker_id) !== workerId) {
      setWorkerId(String(match.worker_id));
    }
  }, [selectedNode?.id, selectedNode?.def.service, handlers]);

  const workerOptions = React.useMemo(() => {
    const base = [{ value: '', label: '— Select worker —' }];
    const opts = workers
      .slice()
      .sort((a, b) => a.worker_key.localeCompare(b.worker_key))
      .map((w) => ({ value: String(w.worker_id), label: `${w.worker_key} (${w.transport})` }));
    return [...base, ...opts];
  }, [workers]);

  const handlerOptions = React.useMemo(() => {
    const base = [{ value: '', label: '— Select handler —' }];
    const opts = buildHandlerOptionsForWorker(handlers, Number(workerId));
    return [...base, ...opts];
  }, [handlers, workerId]);

  const selectedHandler = React.useMemo(() => {
    const key = selectedNode?.def.service?.trim();
    if (!key) return null;
    return resolveHandlerRef(handlers, key);
  }, [handlers, selectedNode?.def.service]);

  // Income definitions migrated to scope-based architecture (workflow.income namespace).
  // Preset/income key list will be populated from scopes in a future iteration.
  const incomeKeysForHandler: string[] = [];

  const handlerConfig = React.useMemo(() => normalizeHandlerConfig(selectedHandler?.config), [selectedHandler?.config]);
  const handlerCfgObj = React.useMemo(() => coerceObject(handlerConfig), [handlerConfig]);
  const handlerParamsSchema = React.useMemo(() => coerceObject(handlerCfgObj.params_schema), [handlerCfgObj]);
  const handlerSchemaParamKeys = React.useMemo(() => {
    return listParamKeysFromParamsSchema(handlerParamsSchema);
  }, [handlerParamsSchema]);
  const handlerSchemaParamKeySet = React.useMemo(() => {
    return new Set(handlerSchemaParamKeys);
  }, [handlerSchemaParamKeys]);
  const handlerParamKeys = React.useMemo(() => {
    const defaults = tryParseJsonObject(selectedNode?.paramsDefaultsJson ?? '{}');
    const bindings = tryParseJsonObject(selectedNode?.paramsBindingsJson ?? '{}');
    const extra = Array.from(new Set([...Object.keys(defaults), ...Object.keys(bindings)]))
      .filter((k) => !handlerSchemaParamKeySet.has(k))
      .sort((a, b) => a.localeCompare(b));
    // Prefer schema-defined keys (stable UX), but always show anything currently wired.
    return [...handlerSchemaParamKeys, ...extra];
  }, [handlerSchemaParamKeys, handlerSchemaParamKeySet, selectedNode?.paramsDefaultsJson, selectedNode?.paramsBindingsJson]);

  const handlerRequiredParams = React.useMemo(() => {
    const keys = listRequiredParamsFromParamsSchema(handlerParamsSchema);
    if (keys.size > 0) return keys;
    // Back-compat if a handler uses JSON Schema required[]
    return listSchemaRequired(handlerParamsSchema);
  }, [handlerParamsSchema]);
  const inputsSchemaObj = React.useMemo(() => tryParseJsonObject(inputsSchemaJson), [inputsSchemaJson]);
  const runtimeInputKeys = React.useMemo(() => listSchemaProperties(inputsSchemaObj), [inputsSchemaObj]);
  const hasRuntimeInputs = runtimeInputKeys.length > 0;

  const otherSteps = React.useMemo(() => nodes.filter((n) => n.id !== selectedNode?.id), [nodes, selectedNode?.id]);
  const outputsByStepId = React.useMemo(() => {
    const map = new Map<string, string[]>();
    nodes.forEach((n) => {
      const handler = resolveHandlerRef(handlers, n.def.service || '');
      map.set(n.id, listHandlerOutputs(handler?.config));
    });
    return map;
  }, [nodes, handlers]);

  const legacyCreateMigration = React.useMemo(() => {
    if (!selectedNode || !selectedHandler) {
      return { show: false, migrate: () => {} };
    }

    const handlerKey = (selectedHandler.handler_key ?? '').trim();
    const isCreateHandler = handlerKey.startsWith('system.handler.create') || handlerKey.startsWith('system.orchestrator.create_handler');
    if (!isCreateHandler) {
      return { show: false, migrate: () => {} };
    }

    const schemaHasConfig = handlerSchemaParamKeySet.has('config');
    if (!schemaHasConfig) {
      return { show: false, migrate: () => {} };
    }

    const defaultsObj = tryParseJsonObject(selectedNode.paramsDefaultsJson);
    const bindingsObj = tryParseJsonObject(selectedNode.paramsBindingsJson);

    const hasConfig = Object.prototype.hasOwnProperty.call(defaultsObj, 'config') || Object.prototype.hasOwnProperty.call(bindingsObj, 'config');
    if (hasConfig) {
      return { show: false, migrate: () => {} };
    }

    const hasLegacyIo = (Object.prototype.hasOwnProperty.call(defaultsObj, 'io') || Object.prototype.hasOwnProperty.call(bindingsObj, 'io'))
      && !handlerSchemaParamKeySet.has('io');
    const hasLegacyMetadata = (Object.prototype.hasOwnProperty.call(defaultsObj, 'metadata') || Object.prototype.hasOwnProperty.call(bindingsObj, 'metadata'))
      && !handlerSchemaParamKeySet.has('metadata');

    if (!hasLegacyIo && !hasLegacyMetadata) {
      return { show: false, migrate: () => {} };
    }

    const ioBinding = bindingsObj.io;
    const mdBinding = bindingsObj.metadata;

    let upstreamStepId = '';
    if (typeof ioBinding === 'string' && bindingKind(ioBinding) === 'upstream_output') {
      upstreamStepId = getUpstreamRef(ioBinding).stepId;
    } else if (typeof mdBinding === 'string' && bindingKind(mdBinding) === 'upstream_output') {
      upstreamStepId = getUpstreamRef(mdBinding).stepId;
    }

    const upstreamOutputs = upstreamStepId ? (outputsByStepId.get(upstreamStepId) ?? []) : [];
    const canUseCreateParams = upstreamStepId && upstreamOutputs.includes('handler_create_params');

    // Prefer the richer upstream output if available:
    // - handler_create_params includes { config: {...}, metadata: {...}, ... }
    // and system.handler.create can extract handler_config from config.config (wrapper).
    const proposedConfigBinding = canUseCreateParams
      ? `{{ steps.${upstreamStepId}.outputs.handler_create_params }}`
      : (typeof ioBinding === 'string' ? ioBinding : null);

    const show = typeof proposedConfigBinding === 'string' && proposedConfigBinding.trim() !== '';

    const migrate = () => {
      const nextDefaults = tryParseJsonObject(selectedNode.paramsDefaultsJson);
      const nextBindings = tryParseJsonObject(selectedNode.paramsBindingsJson);

      if (typeof proposedConfigBinding === 'string' && proposedConfigBinding.trim()) {
        nextBindings.config = proposedConfigBinding;
      }

      // Remove legacy fields when they are not part of the schema.
      if (!handlerSchemaParamKeySet.has('io')) {
        delete nextBindings.io;
        delete nextDefaults.io;
      }

      // Only drop metadata if we can preserve it via handler_create_params.
      if (canUseCreateParams && !handlerSchemaParamKeySet.has('metadata')) {
        delete nextBindings.metadata;
        delete nextDefaults.metadata;
      }

      onUpdate({ paramsDefaultsJson: formatJson(nextDefaults), paramsBindingsJson: formatJson(nextBindings) });
    };

    return { show, migrate };
  }, [selectedNode?.id, selectedHandler?.handler_key, handlerSchemaParamKeySet, outputsByStepId]);

  React.useEffect(() => {
    if (!selectedNode) return;
    // Initialize default drafts for current defaults.
    const defaultsObj = tryParseJsonObject(selectedNode.paramsDefaultsJson);
    const next: Record<string, string> = {};
    Object.entries(defaultsObj).forEach(([k, v]) => {
      next[k] = JSON.stringify(v);
    });
    setDefaultDraftByParam(next);
    setSourceDraftByParam({});
    setRuntimeKeyDraftByParam({});
    setUpstreamDraftByParam({});
  }, [selectedNode?.id]);

  // Guardrail: only allow bindings we can represent safely in the UI.
  // Preserve runtime input / upstream output templates (including supported wrappers like sanitize/date prefix).
  // If an unsupported expression exists, migrate it into defaults (best-effort JSON parse) and remove the binding.
  React.useEffect(() => {
    if (!selectedNode) return;
    const bindingsObj = tryParseJsonObject(selectedNode.paramsBindingsJson);
    const defaultsObj = tryParseJsonObject(selectedNode.paramsDefaultsJson);
    let changed = false;

    Object.entries(bindingsObj).forEach(([param, value]) => {
      if (typeof value !== 'string') return;
      const v = value.trim();
      const kind = bindingKind(v);
      if (kind === 'runtime_input' || kind === 'upstream_output' || kind === 'preset' || kind === 'device_config') return;

      // Move to defaults
      if (!Object.prototype.hasOwnProperty.call(defaultsObj, param)) {
        let parsed: unknown = v;
        try {
          parsed = JSON.parse(v);
        } catch {
          parsed = v;
        }
        defaultsObj[param] = parsed;
      }
      delete bindingsObj[param];
      changed = true;
    });

    if (changed) {
      onUpdate({ paramsDefaultsJson: formatJson(defaultsObj), paramsBindingsJson: formatJson(bindingsObj) });
    }
  }, [selectedNode?.id]);

  // Draft is owned by the parent (so the graph can live-preview it).

  const handlerCatalogConfigJson = React.useMemo(() => {
    if (!selectedHandler) return '';
    return formatJson(normalizeHandlerConfig(selectedHandler.config));
  }, [selectedHandler]);

  if (!selectedNode) {
    return (
      <div className="pac-glass rounded-md p-3 space-y-2">
        <h4 className="text-sm font-semibold text-neutral-200">Step</h4>
        <p className="text-sm text-neutral-400">Select a step.</p>
      </div>
    );
  }

  return (
    <div className="pac-glass rounded-md p-3 space-y-3">
      <h4 className="text-sm font-semibold text-neutral-200">Step</h4>

      <LabeledInput
        label="Step key"
        value={stepKeyDraft}
        placeholder="step_1"
        onChange={onChangeStepKeyDraft}
        onBlur={() => onRenameStep(selectedNode.id, stepKeyDraft)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onRenameStep(selectedNode.id, stepKeyDraft);
          }
        }}
        description="This is the step id stored in the workflow definition. Press Enter or blur to apply."
      />

      {(workersError || handlersError) && (
        <p className="text-xs text-amber-400">
          {workersError ? `Workers failed to load: ${workersError.message}` : ''}
          {handlersError ? `Handlers failed to load: ${handlersError.message}` : ''}
        </p>
      )}

      <LabeledSelect
        label="Type"
        value={selectedNode.def.type}
        options={[{ value: 'cloud', label: 'cloud' }, { value: 'local', label: 'local' }]}
        onChange={(v) => onUpdate({ type: v as StepType })}
      />

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <label className="text-xs uppercase tracking-wide text-neutral-400">Run when inputs filled</label>
          {selectedWhenHas.length ? (
            <button
              type="button"
              className="text-[11px] text-neutral-300 hover:text-neutral-100 underline underline-offset-2"
              onClick={() => onUpdate({ when: undefined })}
              title="Clear condition"
            >
              Clear
            </button>
          ) : null}
        </div>

        {workflowInputKeys.length === 0 ? (
          <div className="text-[11px] text-neutral-500 border border-neutral-800 rounded px-2 py-2 pac-glass">
            No inputs are defined in Inputs schema. Define `properties` in the JSON tab (or use Sync) to enable conditions.
          </div>
        ) : (
          <div className="border border-neutral-800 rounded pac-glass px-2 py-2 max-h-[180px] overflow-auto">
            <div className="grid grid-cols-1 gap-1">
              {workflowInputKeys.map((k) => {
                const isChecked = selectedWhenHas.includes(k);
                const isRequired = listSchemaRequired(inputsSchema).has(k);
                const isString = schemaPropertyIsString(inputsSchema, k);
                return (
                  <label key={k} className="flex items-center gap-2 text-sm text-neutral-200 select-none">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => toggleWhenKey(k, e.target.checked)}
                      className="h-4 w-4 accent-indigo-500"
                    />
                    <span className="min-w-0 truncate" title={k}>{k}</span>
                    <span className="text-[10px] text-neutral-500 whitespace-nowrap">
                      {isRequired ? 'required' : 'optional'}{isString ? ' • string' : ''}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-[11px] leading-snug text-neutral-500">
          If set, this step only exists in the run when ALL checked inputs are filled (non-null; strings must be non-empty). If the condition fails, this step and all downstream steps are excluded.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <LabeledSelect
          label="Worker"
          value={workerId}
          options={workerOptions}
          onChange={(v) => {
            setWorkerId(v);
            // Worker change means handler selection likely changes.
            onUpdate({ service: '' });
          }}
          description={workersLoading ? 'Loading workers…' : 'Choose a worker, then a handler.'}
        />
        <LabeledSelect
          label="Handler"
          value={selectedNode.def.service}
          options={handlerOptions}
          onChange={(v) => {
            const nextKey = v.trim();
            if (!nextKey) {
              onUpdate({ service: '' });
              return;
            }
            onUpdate({ service: preferLatestHandlerRef(nextKey) });
          }}
          description={handlersLoading ? 'Loading handlers…' : (workerId ? 'Service will be set to handler_key.' : 'Select a worker first.')}
        />
      </div>

      {selectedHandler && (
        <div className="text-[11px] text-neutral-400 border border-neutral-800 rounded px-2 py-2 pac-glass">
          <div><span className="text-neutral-500">ability:</span> {selectedHandler.ability_key ?? '—'}</div>
          <div><span className="text-neutral-500">timeout:</span> {selectedHandler.timeout_sec}s</div>
          <div><span className="text-neutral-500">retries:</span> {selectedHandler.max_retries} (backoff {selectedHandler.backoff_ms}ms)</div>
        </div>
      )}

      {selectedHandler && handlerParamKeys.length > 0 && (
        <div className="border border-neutral-800 rounded p-2 pac-glass space-y-2">
          <div className="text-xs uppercase tracking-wide text-neutral-400">Param wiring</div>
          {legacyCreateMigration.show && (
            <div className="text-[11px] text-neutral-400">
              Legacy io/metadata bindings detected for this handler version. This schema expects config.
              <button
                type="button"
                className="ml-2 underline text-neutral-300 hover:text-neutral-100"
                onClick={legacyCreateMigration.migrate}
              >
                Migrate to config
              </button>
            </div>
          )}
          <div className="grid grid-cols-12 gap-2 text-[11px] text-neutral-400 px-1">
            <div className="col-span-3">Param</div>
            <div className="col-span-3">Source</div>
            <div className="col-span-5">Value</div>
            <div className="col-span-1">Override</div>
          </div>

          {handlerParamKeys.map((param) => {
            const defaultsObj = tryParseJsonObject(selectedNode.paramsDefaultsJson);
            const bindingsObj = tryParseJsonObject(selectedNode.paramsBindingsJson);
            const allowlist = new Set(tryParseJsonArray(selectedNode.overrideAllowlistJson));
            const paramDef = getParamDefinition(handlerParamsSchema, param);
            const isInSchema = handlerSchemaParamKeySet.has(param);

            const bindingVal = bindingsObj[param];
            const hasBinding = Object.prototype.hasOwnProperty.call(bindingsObj, param);
            const hasDefault = Object.prototype.hasOwnProperty.call(defaultsObj, param);
            const currentSource: ParamSource = hasBinding
              ? bindingKind(bindingVal)
              : (hasDefault ? 'default' : (sourceDraftByParam[param] ?? 'default'));
            const required = handlerRequiredParams.has(param);
            const isDeviceStep = selectedNode.def.type === 'local';

            const runtimeKey = hasBinding && typeof bindingVal === 'string' && currentSource === 'runtime_input'
              ? getRuntimeInputKey(bindingVal)
              : (runtimeKeyDraftByParam[param] ?? '');

            const runtimeKeyValid = !!runtimeKey && runtimeInputKeys.includes(runtimeKey);

            const presetKey = hasBinding && typeof bindingVal === 'string' && currentSource === 'preset'
              ? getPresetKey(bindingVal)
              : (presetKeyDraftByParam[param] ?? '');
            const presetKeyValid = !!presetKey && incomeKeysForHandler.includes(presetKey);

            const devicePath = hasBinding && typeof bindingVal === 'string' && currentSource === 'device_config'
              ? getDevicePath(bindingVal)
              : (devicePathDraftByParam[param] ?? '');

            const upstreamFromBinding = hasBinding && typeof bindingVal === 'string' && currentSource === 'upstream_output'
              ? getUpstreamRef(bindingVal)
              : { stepId: '', outputKey: '' };
            const upstreamDraft = upstreamDraftByParam[param] ?? { stepId: '', outputKey: '' };
            const upstream = (upstreamFromBinding.stepId && upstreamFromBinding.outputKey)
              ? upstreamFromBinding
              : upstreamDraft;

            const clearBindingAndDefault = () => {
              const nextDefaults = { ...defaultsObj };
              const nextBindings = { ...bindingsObj };
              delete nextDefaults[param];
              delete nextBindings[param];
              onUpdate({ paramsDefaultsJson: formatJson(nextDefaults), paramsBindingsJson: formatJson(nextBindings) });
            };

            const setSource = (next: ParamSource) => {
              setSourceDraftByParam((prev) => ({ ...prev, [param]: next }));
              if (next === 'runtime_input') {
                setRuntimeKeyDraftByParam((prev) => {
                  const existing = (prev[param] ?? '').trim();
                  if (existing && runtimeInputKeys.includes(existing)) return { ...prev, [param]: existing };
                  if (runtimeInputKeys.includes(param)) return { ...prev, [param]: param };
                  if (runtimeInputKeys[0]) return { ...prev, [param]: runtimeInputKeys[0] };
                  return { ...prev, [param]: '' };
                });
              }
              if (next === 'preset') {
                setPresetKeyDraftByParam((prev) => {
                  const existing = (prev[param] ?? '').trim();
                  if (existing && incomeKeysForHandler.includes(existing)) return { ...prev, [param]: existing };
                  if (incomeKeysForHandler.includes(param)) return { ...prev, [param]: param };
                  if (incomeKeysForHandler[0]) return { ...prev, [param]: incomeKeysForHandler[0] };
                  return { ...prev, [param]: '' };
                });
              }
              if (next === 'device_config') {
                setDevicePathDraftByParam((prev) => {
                  const existing = (prev[param] ?? '').trim();
                  if (existing) return { ...prev, [param]: existing };
                  return { ...prev, [param]: 'work_dir' };
                });
              }
              const nextDefaults = { ...defaultsObj };
              const nextBindings = { ...bindingsObj };
              if (next === 'default') {
                delete nextBindings[param];
                if (!Object.prototype.hasOwnProperty.call(nextDefaults, param)) {
                  const hasCatalogDefault = Object.prototype.hasOwnProperty.call(paramDef, 'default') && paramDef.default !== undefined;
                  // If the handler doesn't declare a default, do NOT invent a value.
                  // This keeps required params truly required at workflow-template time.
                  if (hasCatalogDefault) {
                    const nextVal = paramDef.default;
                    nextDefaults[param] = nextVal;
                    setDefaultDraftByParam((prev) => ({ ...prev, [param]: JSON.stringify(nextVal) }));
                  } else {
                    setDefaultDraftByParam((prev) => ({ ...prev, [param]: '' }));
                  }
                }
              } else {
                delete nextDefaults[param];
                delete nextBindings[param];

                // Persist the choice so it doesn't vanish and duplication carries it.
                if (next === 'runtime_input') {
                  const preferred = (runtimeKeyDraftByParam[param] ?? '').trim();
                  const key = runtimeInputKeys.includes(preferred)
                    ? preferred
                    : (runtimeInputKeys.includes(param) ? param : (runtimeInputKeys[0] ?? ''));
                  if (key) {
                    nextBindings[param] = buildRuntimeBinding(key);
                  }
                }
                if (next === 'preset') {
                  const preferred = (presetKeyDraftByParam[param] ?? '').trim();
                  const key = incomeKeysForHandler.includes(preferred)
                    ? preferred
                    : (incomeKeysForHandler.includes(param) ? param : (incomeKeysForHandler[0] ?? ''));
                  if (key) {
                    nextBindings[param] = buildPresetBinding(key);
                  }
                }
                if (next === 'device_config') {
                  const path = (devicePathDraftByParam[param] ?? '').trim() || 'work_dir';
                  nextBindings[param] = buildDeviceBinding(path);
                }
              }
              onUpdate({ paramsDefaultsJson: formatJson(nextDefaults), paramsBindingsJson: formatJson(nextBindings) });
            };

            const setAllowOverride = (checked: boolean) => {
              const arr = Array.from(allowlist);
              const next = checked
                ? Array.from(new Set([...arr, param])).sort()
                : arr.filter((x) => x !== param);
              onUpdate({ overrideAllowlistJson: formatJson(next) });
            };

            const commitDefault = (text: string) => {
              const nextDefaults = { ...defaultsObj };
              let val: unknown = text;
              try {
                val = JSON.parse(text);
              } catch {
                // if user typed a bare word, treat as string
                val = text;
              }
              nextDefaults[param] = val;
              onUpdate({ paramsDefaultsJson: formatJson(nextDefaults) });
            };

            const setBinding = (binding: string) => {
              const nextDefaults = { ...defaultsObj };
              const nextBindings = { ...bindingsObj, [param]: binding };
              delete nextDefaults[param];
              onUpdate({ paramsDefaultsJson: formatJson(nextDefaults), paramsBindingsJson: formatJson(nextBindings) });
            };

            const maybeEnsureUpstreamEdge = (fromStepId: string, toStepId: string) => {
              if (!onEnsureEdge) return;
              const from = (fromStepId ?? '').trim();
              const to = (toStepId ?? '').trim();
              if (!from || !to || from === to) return;
              onEnsureEdge(from, to);
            };

            return (
              <div key={param} className="grid grid-cols-12 gap-2 items-start px-1">
                <div className="col-span-3 text-neutral-200">
                  <div className="truncate" title={param}>{param}</div>
                  <div className="text-[10px] text-neutral-500">
                    {required ? 'required' : ''}
                    {!isInSchema ? (required ? ' • extra' : 'extra') : ''}
                  </div>
                </div>

                <div className="col-span-3">
                  <select
                    value={currentSource}
                    onChange={(e) => setSource(e.target.value as ParamSource)}
                    className="w-full bg-black border border-neutral-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="default">Default (template)</option>
                    <option value="runtime_input" disabled={!hasRuntimeInputs}>
                      Runtime input{hasRuntimeInputs ? '' : ' (define inputs in IO tab)'}
                    </option>
                    <option value="preset" disabled={incomeKeysForHandler.length === 0}>
                      Preset / incomes{incomeKeysForHandler.length > 0 ? '' : ' (none for handler)'}
                    </option>
                    <option value="device_config" disabled={!isDeviceStep}>
                      Device config{isDeviceStep ? '' : ' (local steps only)'}
                    </option>
                    <option value="upstream_output">Upstream output</option>
                  </select>
                </div>

                <div className="col-span-5">
                  {currentSource === 'default' && (
                    <div>
                      <input
                        value={defaultDraftByParam[param] ?? (hasDefault ? JSON.stringify(defaultsObj[param]) : '')}
                        onChange={(e) => setDefaultDraftByParam((prev) => ({ ...prev, [param]: e.target.value }))}
                        onBlur={() => commitDefault(defaultDraftByParam[param] ?? '')}
                        placeholder={
                          Object.prototype.hasOwnProperty.call(paramDef, 'default') && paramDef.default !== undefined
                            ? JSON.stringify(paramDef.default)
                            : 'JSON value (e.g. "pow2", 123, true)'
                        }
                        className="w-full bg-black border border-neutral-700 rounded px-2 py-1 text-xs font-mono outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                      <div className="text-[10px] text-neutral-500 mt-1">
                        {paramDef.type ? <span>type: {paramDef.type}</span> : <span>type: (unspecified)</span>}
                        {paramDef.description ? <span> • {paramDef.description}</span> : null}
                        {typeof paramDef.default !== 'undefined' ? (
                          <span> • default: {String(paramDef.default)}</span>
                        ) : null}
                      </div>
                    </div>
                  )}

                  {currentSource === 'runtime_input' && (
                    <div>
                      <select
                        value={runtimeKeyValid ? runtimeKey : ''}
                        onChange={(e) => {
                          const k = e.target.value;
                          setRuntimeKeyDraftByParam((prev) => ({ ...prev, [param]: k }));
                          if (k) {
                            const t = getRuntimeInputTransforms(bindingVal);
                            setBinding(buildRuntimeBinding(k, t));
                          }
                        }}
                        className="w-full bg-black border border-neutral-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                      >
                        <option value="">— select input —</option>
                        {!runtimeKeyValid && runtimeKey ? (
                          <option value={runtimeKey}>
                            (missing in inputs_schema) {runtimeKey}
                          </option>
                        ) : null}
                        {runtimeInputKeys.map((k) => (
                          <option key={k} value={k}>
                            {k}
                          </option>
                        ))}
                      </select>
                      {!hasRuntimeInputs && (
                        <div className="text-[10px] text-amber-300 mt-1">
                          No workflow inputs are defined. Add inputs in the IO tab first.
                        </div>
                      )}

                      {hasRuntimeInputs && runtimeKeyValid && schemaPropertyIsString(inputsSchemaObj, runtimeKey) && (
                        <div className="mt-1 flex gap-4 items-center text-[10px] text-neutral-300">
                          <label className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              className="accent-indigo-500"
                              checked={getRuntimeInputTransforms(bindingVal).sanitize}
                              onChange={(e) => {
                                const next = buildRuntimeBinding(runtimeKey, {
                                  ...getRuntimeInputTransforms(bindingVal),
                                  sanitize: e.target.checked,
                                });
                                setBinding(next);
                              }}
                            />
                            sanitize filename
                          </label>
                          <label className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              className="accent-indigo-500"
                              checked={getRuntimeInputTransforms(bindingVal).datePrefix}
                              onChange={(e) => {
                                const next = buildRuntimeBinding(runtimeKey, {
                                  ...getRuntimeInputTransforms(bindingVal),
                                  datePrefix: e.target.checked,
                                });
                                setBinding(next);
                              }}
                            />
                            prefix date (yymmdd_)
                          </label>
                        </div>
                      )}

                      {hasRuntimeInputs && !runtimeKeyValid && runtimeKey && (
                        <div className="text-[10px] text-amber-300 mt-1">
                          This binding references an input key that is not defined in inputs_schema.
                        </div>
                      )}
                      <div className="text-[10px] text-neutral-500 mt-1">
                        {paramDef.type ? <span>type: {paramDef.type}</span> : <span>type: (unspecified)</span>}
                        {paramDef.description ? <span> • {paramDef.description}</span> : null}
                        {typeof paramDef.default !== 'undefined' ? (
                          <span> • default: {String(paramDef.default)}</span>
                        ) : null}
                      </div>
                    </div>
                  )}

                  {currentSource === 'preset' && (
                    <div>
                      <select
                        value={presetKeyValid ? presetKey : ''}
                        onChange={(e) => {
                          const k = e.target.value;
                          setPresetKeyDraftByParam((prev) => ({ ...prev, [param]: k }));
                          if (k) {
                            const t = getRuntimeInputTransforms(bindingVal);
                            setBinding(buildPresetBinding(k, t));
                          }
                        }}
                        className="w-full bg-black border border-neutral-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                        disabled={incomeKeysForHandler.length === 0}
                      >
                        <option value="">— select income key —</option>
                        {!presetKeyValid && presetKey ? (
                          <option value={presetKey}>
                            (missing in incomes) {presetKey}
                          </option>
                        ) : null}
                        {incomeKeysForHandler.map((k) => (
                          <option key={k} value={k}>{k}</option>
                        ))}
                      </select>

                      {incomeKeysForHandler.length === 0 && (
                        <div className="text-[10px] text-amber-300 mt-1">
                          No incomes defined for this handler. Create them in the Incomes v2 tab (workflow.income scopes).
                        </div>
                      )}

                      {incomeKeysForHandler.length > 0 && presetKeyValid && (
                        <div className="mt-1 flex gap-4 items-center text-[10px] text-neutral-300">
                          <label className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              className="accent-indigo-500"
                              checked={getRuntimeInputTransforms(bindingVal).sanitize}
                              onChange={(e) => {
                                const next = buildPresetBinding(presetKey, {
                                  ...getRuntimeInputTransforms(bindingVal),
                                  sanitize: e.target.checked,
                                });
                                setBinding(next);
                              }}
                            />
                            sanitize filename
                          </label>
                          <label className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              className="accent-indigo-500"
                              checked={getRuntimeInputTransforms(bindingVal).datePrefix}
                              onChange={(e) => {
                                const next = buildPresetBinding(presetKey, {
                                  ...getRuntimeInputTransforms(bindingVal),
                                  datePrefix: e.target.checked,
                                });
                                setBinding(next);
                              }}
                            />
                            prefix date (yymmdd_)
                          </label>
                        </div>
                      )}

                      <div className="text-[10px] text-neutral-500 mt-1">
                        Reads from effective inputs (after presets/input-sets merge).
                      </div>
                    </div>
                  )}

                  {currentSource === 'device_config' && (
                    <div>
                      <select
                        value={devicePath && devicePath.trim() ? devicePath.trim() : ''}
                        onChange={(e) => {
                          const k = e.target.value;
                          setDevicePathDraftByParam((prev) => ({ ...prev, [param]: k }));
                          if (!k) {
                            clearBindingAndDefault();
                            return;
                          }
                          setBinding(buildDeviceBinding(k));
                        }}
                        className="w-full bg-black border border-neutral-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                        disabled={!isDeviceStep}
                      >
                        <option value="">— select device config key —</option>
                        {deviceConfigKeys.map((k) => (
                          <option key={k} value={k}>{k}</option>
                        ))}
                        {devicePath && devicePath.trim() && !deviceConfigKeys.includes(devicePath.trim()) && (
                          <option value={devicePath.trim()}>{devicePath.trim()} (existing)</option>
                        )}
                      </select>
                      {!isDeviceStep && (
                        <div className="text-[10px] text-amber-300 mt-1">Device config bindings only work for local/device steps.</div>
                      )}
                      {isDeviceStep && deviceConfigKeysLoading && (
                        <div className="text-[10px] text-neutral-500 mt-1">Loading device config keys…</div>
                      )}
                      {isDeviceStep && deviceConfigKeysError && (
                        <div className="text-[10px] text-amber-300 mt-1">Failed to load device config keys: {deviceConfigKeysError.message}</div>
                      )}
                      {isDeviceStep && (
                        <div className="text-[10px] text-neutral-500 mt-1">
                          Uses active device config: {'{{device.work_dir}}'}, {'{{device.mounts}}'}, {'{{device.capabilities}}'}.
                        </div>
                      )}
                    </div>
                  )}

                  {currentSource === 'upstream_output' && (
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={upstream.stepId}
                        onChange={(e) => {
                          const stepId = e.target.value;
                          const keys = outputsByStepId.get(stepId) || [];
                          const outputKey = keys[0] || '';
                          setUpstreamDraftByParam((prev) => ({ ...prev, [param]: { stepId, outputKey } }));

                          // Only persist a binding when we have both pieces. Never write an invalid binding.
                          if (stepId && outputKey) {
                            setBinding(`{{steps.${stepId}.outputs.${outputKey}}}`);
                            maybeEnsureUpstreamEdge(stepId, selectedNode.id);
                          } else {
                            clearBindingAndDefault();
                          }
                        }}
                        className="w-full bg-black border border-neutral-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                      >
                        <option value="">— step —</option>
                        {otherSteps.map((s) => (
                          <option key={s.id} value={s.id}>{s.id}</option>
                        ))}
                      </select>

                      <select
                        value={upstream.outputKey}
                        onChange={(e) => {
                          const outputKey = e.target.value;
                          const stepId = upstream.stepId;
                          setUpstreamDraftByParam((prev) => ({ ...prev, [param]: { stepId, outputKey } }));
                          if (stepId && outputKey) {
                            setBinding(`{{steps.${stepId}.outputs.${outputKey}}}`);
                            maybeEnsureUpstreamEdge(stepId, selectedNode.id);
                          } else {
                            clearBindingAndDefault();
                          }
                        }}
                        className="w-full bg-black border border-neutral-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-500"
                        disabled={!upstream.stepId}
                      >
                        <option value="">— output —</option>
                        {(outputsByStepId.get(upstream.stepId) || []).map((k) => (
                          <option key={k} value={k}>{k}</option>
                        ))}
                      </select>

                      {upstream.stepId && (outputsByStepId.get(upstream.stepId) || []).length === 0 && (
                        <div className="col-span-2 text-[10px] text-amber-300">
                          Selected step has no declared outputs in handler catalog.
                        </div>
                      )}
                    </div>
                  )}

                </div>

                <div className="col-span-1 flex items-center justify-center">
                  <input
                    type="checkbox"
                    className="accent-emerald-500"
                    checked={allowlist.has(param)}
                    onChange={(e) => setAllowOverride(e.target.checked)}
                    title="Allow runtime override"
                  />
                </div>
              </div>
            );
          })}

          {handlersLoading && (
            <div className="text-xs text-neutral-500 px-1">Loading handler registry…</div>
          )}
        </div>
      )}

      {selectedHandler && handlerParamKeys.length === 0 && (
        <div className="text-xs text-neutral-500">
          No params_schema detected for this handler. Use Advanced JSON editors below.
        </div>
      )}

      <LabeledInput
        label="Service (handler key)"
        value={selectedNode.def.service}
        placeholder="handler_key"
        onChange={(v) => onUpdate({ service: preferLatestHandlerRef(v) })}
        description="This is persisted into the workflow definition as step.service."
      />

      {(() => {
        const ref = String(selectedNode.def.service ?? '').trim();
        const resolved = String(selectedHandler?.handler_key ?? '').trim();
        if (!ref || !resolved) return null;
        if (ref === resolved) return null;

        const isLatest = /:latest$/i.test(ref);
        const label = isLatest ? 'Resolved :latest to' : 'Resolved to';

        return (
          <div className="text-[11px] text-neutral-500 -mt-2">
            {label}: <span className="text-neutral-300 font-mono">{resolved}</span>
          </div>
        );
      })()}

      {selectedNode.def.type === 'local' && (
        <LabeledInput
          label="Device capability"
          value={selectedNode.def.device_capability || ''}
          placeholder="audio_processing"
          onChange={(v) => onUpdate({ device_capability: v })}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <LabeledInput
          label="Retries (count)"
          value={String(selectedNode.def.retries.count)}
          placeholder="0"
          onChange={(v) => {
            const parsed = Number.parseInt(v || '0', 10);
            onUpdate({ retries: { ...selectedNode.def.retries, count: Number.isFinite(parsed) ? parsed : 0 } });
          }}
        />
        <LabeledInput
          label="Retries (backoff)"
          value={selectedNode.def.retries.backoff}
          placeholder="linear"
          onChange={(v) => onUpdate({ retries: { ...selectedNode.def.retries, backoff: v } })}
        />
      </div>

      <details className="border border-neutral-800 rounded p-2 pac-glass">
        <summary className="cursor-pointer text-xs text-neutral-300 select-none">Advanced JSON</summary>
        <div className="pt-2 space-y-3">
          {selectedHandler ? (
            <LabeledTextarea
              label="Handler catalog config (read-only)"
              value={handlerCatalogConfigJson}
              onChange={() => { /* read-only */ }}
              rows={8}
              readOnly
              description="This is the handler registry/catalog JSON config fetched from the backend."
            />
          ) : null}

          <LabeledTextarea
            label="Params defaults (JSON)"
            value={selectedNode.paramsDefaultsJson}
            onChange={(v) => onUpdate({ paramsDefaultsJson: v })}
            rows={6}
            description="Fixed defaults for this workflow template (policies, stable values)."
          />

          <LabeledTextarea
            label="Params bindings (JSON)"
            value={selectedNode.paramsBindingsJson}
            onChange={(v) => onUpdate({ paramsBindingsJson: v })}
            rows={6}
            description={'Supports {{inputs.<key>}}, {{preset.<key>}}, {{device.<path>}}, {{steps.<step_id>.outputs.<key>}}, and string transforms like {{sanitize_filename(...)}} and {{date_prefix_yymmdd(...)}}.'}
          />

          <LabeledTextarea
            label="Override allowlist (JSON array)"
            value={selectedNode.overrideAllowlistJson}
            onChange={(v) => onUpdate({ overrideAllowlistJson: v })}
            rows={3}
            description={'List of param keys allowed to be overridden at runtime (optional). Example: ["keep_local"].'}
          />
        </div>
      </details>
    </div>
  );
}
